import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Skeleton } from '@/components/ui/skeleton'
import { errorMessage, errorRequestId, isForbiddenError } from '@/lib/errors'
import { cn } from '@/lib/utils'

import { ForbiddenState } from './ForbiddenState'

/**
 * The four states anything that fetches has to render (design board 12).
 *
 * Kept together because the bug is never "we built the loading state wrong" —
 * it is that only the populated state was built at all, and the other three
 * appeared in production as a blank screen.
 */

/**
 * A loading region that announces itself.
 *
 * Every skeleton wraps in this, so the `aria-busy` and the sr-only label are
 * written once. Without them a skeleton is a decorative grey box: a screen
 * reader reports an empty region and the user has no idea anything is coming.
 */
function Loading({
  children,
  testId,
  className,
}: {
  children: React.ReactNode
  testId: string
  className?: string
}) {
  const { t } = useTranslation()

  return (
    <div data-testid={testId} aria-busy="true" aria-live="polite" className={className}>
      <span className="sr-only">{t('common.loading')}</span>
      {children}
    </div>
  )
}

/** Varied line widths, so a skeleton reads as rows of text rather than a grid of bars. */
const LINE_WIDTHS = ['w-3/5', 'w-1/2', 'w-2/3', 'w-5/12', 'w-7/12', 'w-2/5', 'w-1/2', 'w-1/3']

/**
 * A skeleton shaped like the table rows it replaces.
 *
 * Each row is the real row height (`h-row`, so it follows density) with a
 * 12px text line inside, divided by the same hairline as the real rows — so
 * the page does not jump when the data lands. A spinner is easier and causes
 * exactly that jump.
 */
export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <Loading testId="table-skeleton">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="h-row border-border-subtle flex items-center border-b px-3 last:border-b-0"
        >
          <Skeleton className={cn('h-3', LINE_WIDTHS[index % LINE_WIDTHS.length])} />
        </div>
      ))}
    </Loading>
  )
}

/**
 * A skeleton for a row of stat cards: the StatCard's own geometry — label,
 * 28px value, caption — so a KPI row does not grow when its numbers arrive and
 * push the page down at the moment the reader has started looking at it.
 */
export function CardSkeleton({ cards = 3 }: { cards?: number }) {
  return (
    <div
      data-testid="card-skeleton"
      aria-busy="true"
      aria-live="polite"
      className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
    >
      <SrOnlyLoading />
      {Array.from({ length: cards }, (_, index) => (
        <div key={index} className="bg-card flex flex-col gap-2 rounded-lg border p-5 shadow-xs">
          <Skeleton className="my-0.5 h-3.5 w-18" />
          <Skeleton className="my-0.5 h-7 w-36" />
          <Skeleton className="my-0.5 h-3 w-28" />
        </div>
      ))}
    </div>
  )
}

/**
 * A skeleton for a form waiting on the values it will edit.
 *
 * Each field reserves label plus control, so the submit button does not walk
 * down the page as the fields resolve.
 */
export function FormSkeleton({ fields = 3 }: { fields?: number }) {
  return (
    <Loading testId="form-skeleton" className="flex flex-col gap-4">
      {Array.from({ length: fields }, (_, index) => (
        <div key={index} className="flex flex-col gap-1">
          <Skeleton className="my-1 h-3 w-24" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
      ))}
      <Skeleton className="h-9 w-28 rounded-md" />
    </Loading>
  )
}

/**
 * A skeleton for a grid of square images.
 *
 * Tiles are square because the images are, so the grid is the height it will
 * be when they arrive — and each reserves its two caption rows too. The
 * columns are the image grid's: two, three from `md`, four from `xl`.
 */
export function GallerySkeleton({ tiles = 8 }: { tiles?: number }) {
  return (
    <div
      data-testid="gallery-skeleton"
      aria-busy="true"
      aria-live="polite"
      className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4"
    >
      <SrOnlyLoading />
      {Array.from({ length: tiles }, (_, index) => (
        <div key={index} className="overflow-hidden rounded-lg border">
          <Skeleton className="aspect-square w-full rounded-none" />
          <div className="space-y-2 border-t p-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  )
}

/** The announcement, for skeletons whose layout cannot wrap in `Loading`. */
function SrOnlyLoading() {
  const { t } = useTranslation()
  return <span className="sr-only">{t('common.loading')}</span>
}

/**
 * EmptyState: a 48px icon in the disabled text colour, a heading that states
 * the situation, one sentence on what to do, then at most one action. No
 * illustrations, no jokes.
 */
export function EmptyState({
  title,
  description,
  action,
  size = 'default',
}: {
  title: string
  description: string
  action?: React.ReactNode
  /**
   * `compact` is one line: a 24px icon beside the words, left-aligned, for a
   * secondary list inside a panel, where a page-sized empty state would push
   * everything else off screen. The default is the page-level block.
   */
  size?: 'default' | 'compact'
}) {
  if (size === 'compact') {
    return (
      <div className="bg-card flex items-center gap-3 rounded-lg border px-4 py-3">
        <Icon name="inbox" size={24} className="text-foreground-disabled shrink-0" />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">{title}</h3>
          <p className="text-foreground-secondary text-sm">{description}</p>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    )
  }
  return (
    <div className="bg-card flex flex-col items-center justify-center gap-3 rounded-lg border px-8 py-12 text-center shadow-xs">
      <Icon name="inbox" size={48} className="text-foreground-disabled" />
      <h3 className="text-base font-semibold">{title}</h3>
      <p className="text-foreground-secondary max-w-95 text-sm">{description}</p>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  )
}

/**
 * ErrorState: an error the user can act on.
 *
 * The icon and the words carry the error, not the red alone. The request id
 * is shown deliberately, in mono so it can be selected and read back: it is
 * the one string that turns "it broke" into a log line the backend can find.
 *
 * A 403 is not a fault, and gets no Retry: it is a sentence and a **Request
 * access** action instead ({@link ForbiddenState}).
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation()
  if (isForbiddenError(error)) return <ForbiddenState detail={errorMessage(error, '')} />
  const requestId = errorRequestId(error)

  return (
    <div
      role="alert"
      className="bg-card flex flex-col items-center gap-3 rounded-lg border px-8 py-12 text-center shadow-xs"
    >
      <Icon name="error" size={48} className="text-error" />
      <h3 className="text-base font-semibold">
        {errorMessage(error, t('common.somethingWentWrong'))}
      </h3>
      {requestId ? (
        <p className="text-muted-foreground font-mono text-xs select-all">
          {t('common.reference', { id: requestId })}
        </p>
      ) : null}
      {onRetry ? (
        <Button variant="outline" className="mt-1" onClick={onRetry}>
          <Icon name="refresh" size={16} />
          {t('common.retry')}
        </Button>
      ) : null}
    </div>
  )
}
